// `donation.attributed.v1` — emitted by marketing-backend to stamp
// post-donation attribution facts onto the donation row. Two carriers, either
// or both may be present:
//   • `csAttribution` — the Layer-2 LIKELY_INFLUENCED verdict (PE-1653 / M-7.4):
//     the donor received a campaign/trigger send within the attribution window
//     and there is no Layer-1 UTM CONFIRMED match.
//   • `attributionChannel` — the EMAIL/SMS channel that drove the gift, resolved
//     from the touch log for the attributing broadcast. Carried for BOTH Layer-1
//     CONFIRMED click-throughs (donated via an email/SMS link) AND Layer-2
//     influenced gifts, so a Layer-1 donation now also emits this event (channel
//     only, no csAttribution).
//
// transactions-backend CONSUMES this and stamps the verdict onto the donation
// row (`update_item SET csAttribution`), keyed on `transactionID`. The verdict
// then flows into the OpenSearch donation index on the next re-index so the
// marketing dashboard's attribution surfaces can read it.
//
// Producer: marketing-backend (`Source: 'marketing'`).
// Consumer: transactions-backend (the donation_attributed_consumer Lambda).
// detailType in EventBridge: `donation.attributed.v1` (Commitment 6).
//
// Why a SEPARATE event (not a field on donation.completed.v1): the verdict is
// computed AFTER the donation finalizes — Layer-2 needs the touch log and the
// completed donation in hand before it can decide. It is a distinct fact
// ("this donation was attributed") with a distinct producer/consumer pair, so
// it gets its own additive event rather than retrofitting the completion event.

import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';
// Reuse the canonical broadcast-type enum (CAMPAIGN | TRIGGER) rather than
// redefining it — it is the single source of truth for this closed set and
// `index.ts` re-exports both namespaces, so a local redefinition would also
// collide on the barrel.
import { broadcastTypeSchema } from '../marketing/broadcasts.js';

// The attribution verdict carried to the consumer. Stamped verbatim onto the
// donation row as the `csAttribution` map. `method` is a literal: this event
// EXISTS only to carry a LIKELY_INFLUENCED verdict (Layer-1 CONFIRMED is
// compute-on-read over UTM tags and never produces this event). Keeping it a
// literal — rather than dropping the field — makes the stamped row
// self-describing and lets a future Layer-3 method extend the union without a
// silent reinterpretation of existing rows.
export const csAttributionVerdictSchema = z.object({
  broadcastId: z.string().min(1).max(256),
  broadcastType: broadcastTypeSchema,
  method: z.literal('LIKELY_INFLUENCED'),
  // ISO-8601 with offset — when marketing decided the attribution. Distinct
  // from the envelope's `occurredAt` (which is also the decision time today,
  // but the consumer stamps THIS onto the row, so it is named explicitly).
  attributedAt: z.string().datetime({ offset: true }),
});
export type CsAttributionVerdict = z.infer<typeof csAttributionVerdictSchema>;

// Which channel drove this donation — resolved from the contact's touch log
// for the attributing broadcast (the last EMAIL vs SMS send for that broadcast
// before the gift). DELIBERATELY a top-level field, NOT part of csAttribution:
// csAttribution carries a LIKELY_INFLUENCED (Layer-2) verdict, but channel
// applies to Layer-1 CONFIRMED click-throughs too (a donation from an email
// link). Keeping it separate lets a CONFIRMED donation carry a channel without
// being mis-stamped as LIKELY_INFLUENCED. Mixed-channel broadcasts (an email
// step AND an sms step to the same contact) resolve to the last touch before
// the gift.
export const attributionChannelSchema = z.enum(['EMAIL', 'SMS']);
export type AttributionChannel = z.infer<typeof attributionChannelSchema>;

export const donationAttributedV1Schema = eventEnvelopeSchema
  .extend({
    // The donation/transaction this verdict is for. The consumer keys its
    // `update_item` on this (the donations table PK). 1:1 with the donation;
    // equals the producer's `originIdempotencyKey` (the per-charge transactionID)
    // but carried explicitly because a primary-key write must not pigg-back on
    // the envelope's dedupe-key semantics.
    transactionID: z.string().min(1).max(256),
    // OPTIONAL as of the channel-attribution work: a Layer-1 CONFIRMED donation
    // emits this event to carry ONLY `attributionChannel` (no Layer-2 verdict),
    // so csAttribution is no longer always present. The consumer writes each
    // field independently when present. Existing Layer-2 emitters keep sending it.
    csAttribution: csAttributionVerdictSchema.optional(),
    // Present when marketing could resolve the driving channel from the touch
    // log. Absent when unknown (no touch found). Consumer stamps it top-level.
    attributionChannel: attributionChannelSchema.optional(),
  })
  // The event must carry at least one payload beyond the id — otherwise there is
  // nothing for the consumer to stamp. (An event with neither is a producer bug.)
  .refine((e) => e.csAttribution !== undefined || e.attributionChannel !== undefined, {
    message: 'donation.attributed.v1 must carry csAttribution and/or attributionChannel',
  });

export type DonationAttributedV1 = z.infer<typeof donationAttributedV1Schema>;

export const DONATION_ATTRIBUTED_V1_DETAIL_TYPE = 'donation.attributed.v1' as const;
